import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fmtApiError } from "@/lib/adminApi";
import { inputCls } from "./shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminSettings() {
    const [settings, setSettings] = useState(null);
    const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/settings").then((r) => setSettings(r.data)).catch(() => toast.error("Could not load settings"));
    }, []);

    const changePassword = async (e) => {
        e.preventDefault();
        if (form.new_password !== form.confirm) return toast.error("New passwords do not match");
        setSaving(true);
        try {
            await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
            toast.success("Password updated");
            setForm({ current_password: "", new_password: "", confirm: "" });
        } catch (err) {
            toast.error(fmtApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-2xl space-y-6" data-testid="admin-settings">
            <div>
                <h1 className="font-serif text-3xl font-semibold text-ink">Settings</h1>
                <p className="mt-1 text-sm text-muted-foreground">Account and payment gateway configuration</p>
            </div>

            <div className="rounded-2xl border border-border bg-white p-6 card-shadow" data-testid="settings-account">
                <h2 className="font-serif text-xl font-semibold text-ink">Admin Account</h2>
                <p className="mt-3 text-sm text-muted-foreground">Signed in as <span className="font-medium text-ink">{settings?.admin_email}</span></p>
                <form onSubmit={changePassword} className="mt-6 space-y-4" data-testid="change-password-form">
                    <div className="space-y-1.5">
                        <Label>Current password</Label>
                        <Input type="password" required value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className={inputCls} data-testid="current-password-input" />
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-1.5">
                            <Label>New password</Label>
                            <Input type="password" required minLength={8} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className={inputCls} data-testid="new-password-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Confirm new password</Label>
                            <Input type="password" required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={inputCls} data-testid="confirm-password-input" />
                        </div>
                    </div>
                    <button type="submit" disabled={saving} className="rounded-full bg-saffron px-6 py-2.5 text-sm font-medium text-cream hover:bg-teal disabled:opacity-60" data-testid="change-password-button">
                        {saving ? "Updating…" : "Update Password"}
                    </button>
                </form>
            </div>

            <div className="rounded-2xl border border-border bg-white p-6 card-shadow" data-testid="settings-razorpay">
                <h2 className="font-serif text-xl font-semibold text-ink">Razorpay (Online Payments)</h2>
                <p className="mt-3 flex items-center gap-2 text-sm">
                    Status:
                    <span className={`rounded-full px-3 py-1 text-xs font-medium ${settings?.razorpay_configured ? "bg-teal-soft text-teal" : "bg-amber-100 text-amber-700"}`} data-testid="razorpay-status-badge">
                        {settings?.razorpay_configured ? "Connected" : "Not configured"}
                    </span>
                </p>
                {!settings?.razorpay_configured && (
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                        Online payment links activate once your Razorpay keys are added to the backend environment
                        (RAZORPAY_KEY_ID, RAZORPAY_KEY_SECRET and RAZORPAY_WEBHOOK_SECRET). Until then, record payments
                        manually as cash, UPI or bank transfer — everything else works fully.
                    </p>
                )}
            </div>
        </div>
    );
}
